var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { List, Input, Empty, Dropdown, Menu, Button, Tag, Checkbox } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
var Search = Input.Search;
/** 点击是否来自行内可交互控件（复选框、按钮等），若是则不应触发行 onSelect */
function isClickFromRowInteractiveControl(target) {
    if (target == null || !(target instanceof Element))
        return false;
    return Boolean(target.closest([
        "input",
        "textarea",
        "select",
        "button",
        'a[href]',
        '[role="checkbox"]',
        '[role="switch"]',
        ".ant-checkbox",
        ".ant-checkbox-wrapper",
        ".ant-checkbox-input",
        ".ant-switch",
        ".ant-radio",
        ".ant-radio-wrapper",
        ".list-item-menu-button",
    ].join(",")));
}
var extractNumberFromContent = function (content) {
    var _a;
    if (typeof content === "number")
        return content.toString();
    if (typeof content === "string")
        return content;
    if (React.isValidElement(content)) {
        var children = (_a = content.props) === null || _a === void 0 ? void 0 : _a.children;
        if (children) {
            if (typeof children === "string") {
                var match = children.match(/(\d+)/);
                return match ? match[1] : "0";
            }
            if (typeof children === "number") {
                return children.toString();
            }
        }
    }
    return "0";
};
var CompsList = function (_a) {
    var _b = _a.listData, listData = _b === void 0 ? [] : _b, _c = _a.loading, loading = _c === void 0 ? false : _c, onSelect = _a.onSelect, _d = _a.showSearch, showSearch = _d === void 0 ? true : _d, selectedId = _a.selectedId, rowSelection = _a.rowSelection, _e = _a.menuItems, menuItems = _e === void 0 ? [] : _e, onMenuClick = _a.onMenuClick, _f = _a.height, height = _f === void 0 ? "100%" : _f, style = _a.style, className = _a.className, _g = _a.searchPlaceholder, searchPlaceholder = _g === void 0 ? "搜索..." : _g, _h = _a.defaultSearchValue, defaultSearchValue = _h === void 0 ? "" : _h, _j = _a.draggable, draggable = _j === void 0 ? false : _j, onDragStart = _a.onDragStart, onReorder = _a.onReorder, _k = _a.itemRowAlignItems, itemRowAlignItems = _k === void 0 ? "center" : _k;
    var _l = useState(defaultSearchValue), searchValue = _l[0], setSearchValue = _l[1];
    var _m = useState(null), draggingId = _m[0], setDraggingId = _m[1];
    var filteredListData = useMemo(function () {
        if (!searchValue)
            return listData;
        var lower = searchValue.toLowerCase();
        return listData.filter(function (item) {
            var raw = typeof item.searchText === "string"
                ? item.searchText
                : typeof item.leftContent === "string"
                    ? item.leftContent
                    : "";
            return raw.toLowerCase().includes(lower);
        });
    }, [listData, searchValue]);
    /** 与行 id 比较时统一为字符串，避免 selectedKeys 为 string、item.id 为 number 时 includes 恒为 false */
    var selectedRowKeySet = useMemo(function () { var _a; return new Set(((_a = rowSelection === null || rowSelection === void 0 ? void 0 : rowSelection.selectedRowKeys) !== null && _a !== void 0 ? _a : []).map(function (k) { return String(k); })); }, [rowSelection === null || rowSelection === void 0 ? void 0 : rowSelection.selectedRowKeys]);
    var filteredIdStrings = useMemo(function () { return filteredListData.map(function (i) { return String(i.id); }); }, [filteredListData]);
    var rowSelectionRef = useRef(rowSelection);
    rowSelectionRef.current = rowSelection;
    var applySelectAllFiltered = useCallback(function (checked) {
        var _a;
        var rs = rowSelectionRef.current;
        if (!(rs === null || rs === void 0 ? void 0 : rs.onChange))
            return;
        var currentKeys = (_a = rs.selectedRowKeys) !== null && _a !== void 0 ? _a : [];
        if (checked) {
            var merged = new Set(__spreadArray(__spreadArray([], currentKeys.map(function (k) { return String(k); }), true), filteredIdStrings, true));
            rs.onChange(__spreadArray([], merged, true));
            return;
        }
        var filteredSet = new Set(filteredIdStrings);
        rs.onChange(currentKeys.filter(function (k) { return !filteredSet.has(String(k)); }));
    }, [filteredIdStrings]);
    useEffect(function () {
        // 当上游 listData 变化时，保持当前搜索逻辑
    }, [listData]);
    var renderItemMenu = function (item) {
        if (!onMenuClick || !menuItems.length)
            return null;
        var menu = (_jsx(Menu, __assign({ onClick: function (_a) {
                var key = _a.key;
                onMenuClick && onMenuClick(item, key);
            } }, { children: menuItems.map(function (menuItem) { return (_jsx(Menu.Item, { children: menuItem.label }, menuItem.key)); }) })));
        return (_jsx(Dropdown, __assign({ overlay: menu, trigger: ["click"] }, { children: _jsx(Button, { type: "text", icon: _jsx(EllipsisOutlined, {}), size: "small", onClick: function (e) {
                    e.stopPropagation();
                }, style: {
                    marginLeft: "auto",
                    padding: "0 5px",
                    color: "#bfbfbf",
                }, className: "list-item-menu-button" }) })));
    };
    var fillFlexParent = height === "100%";
    return (_jsxs("div", __assign({ className: className, style: __assign(__assign({ height: typeof height === "number" ? "".concat(height, "px") : height, display: "flex", flexDirection: "column", minHeight: 0 }, (fillFlexParent ? { flex: 1, overflow: "hidden" } : {})), style) }, { children: [showSearch && (_jsx(Search, { placeholder: searchPlaceholder, allowClear: true, value: searchValue, onChange: function (e) { return setSearchValue(e.target.value); }, style: { marginBottom: 16 } })), (rowSelection === null || rowSelection === void 0 ? void 0 : rowSelection.showSelectAll) && filteredListData.length > 0 ? (_jsx("div", __assign({ style: {
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 8,
                    flexShrink: 0,
                }, onMouseDown: function (e) {
                    e.stopPropagation();
                }, onClick: function (e) {
                    e.stopPropagation();
                } }, { children: (function () {
                    var _a;
                    var n = filteredIdStrings.filter(function (id) { return selectedRowKeySet.has(id); }).length;
                    var allFilteredSelected = filteredIdStrings.length > 0 &&
                        filteredIdStrings.every(function (id) { return selectedRowKeySet.has(id); });
                    var someFilteredSelected = n > 0 && n < filteredIdStrings.length;
                    return (_jsxs(_Fragment, { children: [_jsx(Checkbox, __assign({ indeterminate: someFilteredSelected, checked: allFilteredSelected, onChange: function (e) {
                                    applySelectAllFiltered(e.target.checked);
                                } }, { children: (_a = rowSelection.selectAllLabel) !== null && _a !== void 0 ? _a : "全选" })), someFilteredSelected ? (_jsxs("span", __assign({ style: { fontSize: 12, color: "rgba(0,0,0,0.45)" } }, { children: ["\u5DF2\u9009 ", n, " / ", filteredIdStrings.length] }))) : null] }));
                })() }))) : null, _jsx("div", __assign({ style: {
                    flex: 1,
                    minHeight: 0,
                    overflow: "auto",
                } }, { children: filteredListData.length > 0 ? (_jsx(List, { className: "comps-list", dataSource: filteredListData, 
                    /**
                     * 带 rowSelection 时：有行仍传 loading 会让 antd List 叠 Spin 层，常拦截行首复选点击。
                     * 无 rowSelection 的列表保持原 loading 语义（如刷新时整表转圈）。
                     */
                    loading: rowSelection ? loading && filteredListData.length === 0 : loading, renderItem: function (item) {
                        var _a, _b;
                        return (_jsx(List.Item, __assign({ onClick: function (e) {
                                if (!onSelect)
                                    return;
                                if (isClickFromRowInteractiveControl(e.target))
                                    return;
                                onSelect(item);
                            }, draggable: draggable, onDragStart: function (e) {
                                if (draggable && onDragStart) {
                                    setDraggingId(item.id);
                                    onDragStart(item, e);
                                }
                                else if (draggable) {
                                    // 默认处理：将数据存储到 dataTransfer
                                    setDraggingId(item.id);
                                    e.dataTransfer.setData("text/plain", item.id);
                                    e.dataTransfer.effectAllowed = "move";
                                }
                            }, onDragOver: function (e) {
                                if (!draggable)
                                    return;
                                // 仅在未开启搜索时允许拖拽排序，避免过滤列表导致索引错乱
                                if (searchValue)
                                    return;
                                e.preventDefault();
                            }, onDrop: function (e) {
                                if (!draggable || !onReorder)
                                    return;
                                if (searchValue)
                                    return;
                                e.preventDefault();
                                var sourceId = draggingId || e.dataTransfer.getData("text/plain");
                                if (!sourceId || sourceId === item.id)
                                    return;
                                var sourceIndex = listData.findIndex(function (it) { return it.id === sourceId; });
                                var targetIndex = listData.findIndex(function (it) { return it.id === item.id; });
                                if (sourceIndex === -1 || targetIndex === -1)
                                    return;
                                var newList = __spreadArray([], listData, true);
                                var moved = newList.splice(sourceIndex, 1)[0];
                                newList.splice(targetIndex, 0, moved);
                                onReorder(newList);
                                setDraggingId(null);
                            }, style: {
                                cursor: draggable ? "grab" : "default",
                                backgroundColor: selectedId === item.id ? "#f0f7ff" : "transparent",
                                padding: "8px",
                                borderRadius: "4px",
                                transition: "background-color 0.3s",
                            }, className: "comps-list-item" }, { children: _jsxs("div", __assign({ style: {
                                    display: "flex",
                                    justifyContent: "space-between",
                                    width: "100%",
                                    alignItems: itemRowAlignItems,
                                } }, { children: [rowSelection ? (_jsx("span", __assign({ className: "comps-list-row-selection-wrap", style: {
                                            marginRight: 10,
                                            flexShrink: 0,
                                            display: "inline-flex",
                                            alignItems: itemRowAlignItems,
                                            position: "relative",
                                            zIndex: 1,
                                        }, onMouseDown: function (e) {
                                            /** 阻止冒泡到 List.Item / Spin，避免外层拦截指针事件 */
                                            e.stopPropagation();
                                        }, onClick: function (e) {
                                            e.stopPropagation();
                                        } }, { children: _jsx(Checkbox, { checked: selectedRowKeySet.has(String(item.id)), disabled: (_b = (_a = rowSelection.getCheckboxProps) === null || _a === void 0 ? void 0 : _a.call(rowSelection, item)) === null || _b === void 0 ? void 0 : _b.disabled, onChange: function () {
                                                var _a, _b, _c;
                                                var rs = rowSelectionRef.current;
                                                if (!(rs === null || rs === void 0 ? void 0 : rs.onChange))
                                                    return;
                                                if ((_b = (_a = rs.getCheckboxProps) === null || _a === void 0 ? void 0 : _a.call(rs, item)) === null || _b === void 0 ? void 0 : _b.disabled)
                                                    return;
                                                var rawKeys = (_c = rs.selectedRowKeys) !== null && _c !== void 0 ? _c : [];
                                                var idStr = String(item.id);
                                                var wasOn = rawKeys.some(function (k) { return String(k) === idStr; });
                                                var next = wasOn
                                                    ? rawKeys.filter(function (k) { return String(k) !== idStr; })
                                                    : __spreadArray(__spreadArray([], rawKeys, true), [item.id], false);
                                                rs.onChange(next);
                                            } }) }))) : null, _jsxs("div", __assign({ style: {
                                            display: "flex",
                                            alignItems: itemRowAlignItems,
                                            fontWeight: selectedId === item.id ? 500 : 400,
                                            justifyContent: "space-between",
                                            flex: 1,
                                            minWidth: 0,
                                            cursor: onSelect ? "pointer" : "default",
                                        }, onClick: onSelect
                                            ? function () {
                                                onSelect(item);
                                            }
                                            : undefined, role: onSelect ? "button" : undefined }, { children: [_jsx("div", __assign({ style: { marginRight: 8, minWidth: 0, flex: 1 } }, { children: item.leftContent })), item.rightContent && (_jsx(_Fragment, { children: React.isValidElement(item.rightContent) ? (item.rightContent) : (function () {
                                                    var extractedNumber = extractNumberFromContent(item.rightContent);
                                                    var isNumber = typeof item.rightContent === "number" ||
                                                        (typeof item.rightContent === "string" && /^\d+$/.test(item.rightContent)) ||
                                                        (extractedNumber !== "0" && extractedNumber !== "");
                                                    if (isNumber) {
                                                        return (_jsx(Tag, __assign({ color: "blue", style: { marginInlineEnd: 0 } }, { children: extractedNumber })));
                                                    }
                                                    else {
                                                        return item.rightContent;
                                                    }
                                                })() }))] })), renderItemMenu(item)] })) }), item.id));
                    } })) : (_jsx(Empty, { description: "\u6682\u65E0\u6570\u636E" })) }))] })));
};
export default CompsList;
